import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DarkBand } from '@/components/marketing/section'
import { HeroStage } from '@/components/marketing/hero-stage'
import { ProbeBlips } from '@/components/marketing/probe-blips'
import { OutageTimeline } from '@/components/marketing/outage-timeline'
import { LiveStats } from '@/components/marketing/live-stats'
import { TextLink } from '@/components/marketing/text-link'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'
import { useFreePlan } from '@/hooks/use-free-plan'

function CtaButtons({ className = '' }: { className?: string }) {
  const free = useFreePlan()
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <StartFreeButton />
      {/* One main button: "See plans" steps back to outline while "Start free" is shown. */}
      <Button size="lg" variant={free.isOpen ? 'outline' : 'default'} className="h-11 px-5 text-[15px]" asChild>
        <Link to="/pricing">
          See plans
          {!free.isOpen && <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />}
        </Link>
      </Button>
      <TextLink to="/features/browser-monitoring">How browser checks work</TextLink>
    </div>
  )
}

export function Hero() {
  // The red line across "minutes" follows the timeline: it is drawn when the example site is fixed.
  const [struck, setStruck] = useState(false)
  return (
    <DarkBand>
      {/* Words on the left, product stage on the right: people read left to right.
          The band is dark in both themes, so the section carries `dark` to give its
          text and buttons dark-theme colours. On small screens the stage stacks
          under the words. The whole stage sits above the fold on a laptop. */}
      <div className="relative container mx-auto grid items-center gap-10 px-4 pb-6 pt-10 md:pt-14 lg:grid-cols-12 lg:gap-8 lg:pb-4 lg:pt-10">
        <div className="flex flex-col items-start text-left lg:col-span-5">
          {/* One exception to "no two-tone headlines" (owner decision 2026-10-14, DESIGN.md). */}
          <h1 className="max-w-xl text-balance text-[2.5rem] font-semibold leading-[1.08] tracking-[-0.035em] md:text-display">
            Know your website is down in <span className="text-primary">seconds</span>, not <span className={`headline-strike${struck ? ' is-struck' : ''}`}>minutes</span>.
          </h1>
          <p className="mt-4 max-w-[30em] text-lead text-body">
            HTTP checks run every 30 seconds from up to 24 locations. Browser checks for login and checkout are also
            included.
          </p>
          {/* Under the sub text; on a short window it moves below the buttons so Start free stays above the fold. */}
          <div className="mt-7 w-full max-w-[560px] [@media(max-height:720px)]:order-last [@media(max-height:720px)]:mt-8">
            <OutageTimeline onStruckChange={setStruck} />
          </div>
          <CtaButtons className="mt-6" />
          <PlanFinePrint className="mt-5" />
        </div>
        <div className="min-w-0 lg:col-span-7">
          <HeroStage />
          <LiveStats className="mt-4 sm:hidden" />
          <p className="mt-3 text-right text-sm text-muted-foreground">Dashboard screenshot, alerts and failing-location markers are example data; the live counters are real.</p>
        </div>
      </div>
      {/* The probe strip: its own row at the bottom of the band, under the words and the stage. */}
      <div className="relative container mx-auto px-4 pb-8 lg:pb-10">
        <ProbeBlips />
      </div>
    </DarkBand>
  )
}
